/**
 * A single cited source extracted from an assistant message's trailing
 * "Sources" section. `href` is present for Markdown-link rows (content-library /
 * tender `mytender-source:` links or web URLs) and absent for plain-text rows.
 */
export type MessageSource = {
  title: string;
  href?: string;
};

export type ParsedMessageSources = {
  /** The message text with the trailing "Sources" section removed. */
  body: string;
  /** The parsed sources, in the order they appeared. */
  sources: MessageSource[];
};

// A line that is just a "Sources" heading: optional list/heading/emphasis
// markers around the word "Sources", optionally followed by a colon.
const SOURCES_HEADING_RE =
  /^\s*(?:#{1,6}\s+)?(?:[-*+]\s+)?(?:\*\*|__|\*|_)?\s*sources\s*(?:\*\*|__|\*|_)?\s*:?\s*$/i;

// Compact one-liner the model sometimes emits instead of a list:
// "Sources: File A.docx | File B.pdf" or with Markdown links after the colon.
const INLINE_SOURCES_LINE_RE =
  /^\s*(?:#{1,6}\s+)?(?:[-*+]\s+)?(?:\*\*|__|\*|_)?\s*sources\s*(?:\*\*|__|\*|_)?\s*:\s+(\S.*)$/i;

// A Markdown list item: "- ...", "* ...", "+ ...", "1. ..." or "1) ...".
const LIST_ITEM_RE = /^(?:[-*+]|\d+[.)])\s+(.*)$/;

// While a reply streams, its last line when it may yet become a Sources
// heading or a compact "Sources: ..." line: heading, bullet or emphasis markers
// (each on its own too), a prefix of the word "sources", then the heading's
// closing markers or a colon and whatever follows. Hidden until the line is
// finished, so the chat never shows it and then takes it back. A numbered
// marker is left out on purpose: "2" can end a reply, so it has to show, and
// hiding "2." a token later would take it back.
const SOURCES_TAIL_RE =
  /^\s*(?:#{1,6}\s*)?(?:[-*+]\s*)?(?:\*\*|__|\*|_)?\s*(?:s|so|sou|sour|sourc|source|sources)?\s*(?:\*\*|__|\*|_)?\s*(?::.*)?$/i;

// A numbered list marker being written: "1", "1." or "1)". Hidden only under a
// Sources heading or a removed source line, where it can only be the next item.
const PARTIAL_NUMBER_RE = /^\s*\d+[.)]?\s*$/;

// The first Markdown link within a line: [title](href).
const MARKDOWN_LINK_RE = /\[([^\]]+)\]\(([^)\s]+)\)/;

const looksLikeInlineSourcesRest = (rest: string): boolean => {
  if (rest.includes("|")) return true;
  if (MARKDOWN_LINK_RE.test(rest)) return true;
  // Bare filename(s) on the same line as "Sources:".
  return /\.(docx?|pdf|pptx?|xlsx?|txt|md|csv)\b/i.test(rest);
};

const parseSourceItem = (itemText: string): MessageSource | null => {
  const trimmed = itemText.trim();
  if (!trimmed) return null;
  // Allow nested brackets in the link title (e.g. "1.8 [Company Name] - …docx")
  // by anchoring on the final "](href)" rather than the first "]".
  const openLink = trimmed.lastIndexOf("](");
  if (openLink > 0 && trimmed.startsWith("[") && trimmed.endsWith(")")) {
    const title = trimmed.slice(1, openLink).trim();
    const href = trimmed.slice(openLink + 2, -1).trim();
    if (title && href && !/\s/.test(href)) {
      return { title, href };
    }
  }
  const linkMatch = trimmed.match(MARKDOWN_LINK_RE);
  if (linkMatch) {
    return { title: linkMatch[1].trim(), href: linkMatch[2].trim() };
  }
  return { title: trimmed };
};

const parseInlineSourcesLine = (line: string): MessageSource[] | null => {
  const match = line.match(INLINE_SOURCES_LINE_RE);
  if (!match) return null;
  const rest = match[1].trim();
  if (!looksLikeInlineSourcesRest(rest)) return null;

  const segments = rest.includes("|") ? rest.split("|") : [rest];
  const sources = segments
    .map(parseSourceItem)
    .filter((source): source is MessageSource => source != null);
  return sources.length > 0 ? sources : null;
};

/**
 * Split an assistant message into its answer body and a trailing "Sources"
 * section. Agents are instructed to finish a sourced answer with a "Sources"
 * heading followed by a list of Markdown links; this pulls that block out so the
 * UI can render it separately (a small hover) instead of inline in the message.
 *
 * Also strips compact one-line forms the model sometimes emits at the top or
 * bottom of a reply (`Sources: A | B | C`), which otherwise leak into the body
 * while the footer "Sources" button already shows the same citations.
 *
 * The section is recognised as a "Sources" heading immediately followed by one
 * or more list items (blank lines allowed between them). The heading + those
 * list items are removed from the body — even when the model added a trailing
 * follow-up line after the list (e.g. "Want me to…?"), which is kept — so the
 * citations only ever render in the separate hover, never inline. A "Sources"
 * heading NOT followed by any list item (a mid-answer heading in prose) is left
 * untouched.
 *
 * The chat runs this on every streamed partial and types the result out
 * smoothly, restarting from the top whenever the result stops being an
 * extension of what it has shown. So while `streaming`, the output only ever
 * grows as text is appended: a last line that may still become a "Sources"
 * heading or a compact "Sources: ..." line is hidden until it is finished,
 * and a list marker under such a heading goes with it. Once the reply is
 * complete the rules above decide alone. Either way the body is normalised
 * the same way whether or not anything was removed (runs of blank lines
 * collapsed, leading blank lines and trailing whitespace trimmed), so it does
 * not change shape the moment a Sources block first gets removed.
 * @param text - The full assistant message text (may span multiple parts).
 * @param options - Parsing options.
 * @param options.streaming - Whether the text is still being written.
 * @returns The body with the section stripped and the parsed sources.
 */
export function parseMessageSources(
  text: string,
  { streaming = false }: { streaming?: boolean } = {}
): ParsedMessageSources {
  if (!text) return { body: text ?? "", sources: [] };

  const lines = text.split("\n");
  const dropIndexes = new Set<number>();
  const sources: MessageSource[] = [];

  // Compact "Sources: A | B | C" lines anywhere in the message.
  for (let i = 0; i < lines.length; i += 1) {
    const inline = parseInlineSourcesLine(lines[i]);
    if (!inline) continue;
    for (const source of inline) sources.push(source);
    dropIndexes.add(i);
  }

  // Classic trailing / mid-message "Sources" heading + list items.
  let headingIndex = -1;
  for (let i = lines.length - 1; i >= 0; i -= 1) {
    if (dropIndexes.has(i)) continue;
    if (SOURCES_HEADING_RE.test(lines[i])) {
      headingIndex = i;
      break;
    }
  }

  if (headingIndex !== -1) {
    const listSources: MessageSource[] = [];
    let lastConsumed = headingIndex;
    for (let i = headingIndex + 1; i < lines.length; i += 1) {
      if (dropIndexes.has(i)) continue;
      const line = lines[i].trim();
      if (line === "") continue;

      const listMatch = line.match(LIST_ITEM_RE);
      if (!listMatch) break;

      const item = parseSourceItem(listMatch[1]);
      if (item) listSources.push(item);
      lastConsumed = i;
    }

    if (listSources.length > 0) {
      for (const source of listSources) sources.push(source);
      for (let i = headingIndex; i <= lastConsumed; i += 1) {
        dropIndexes.add(i);
      }
    }
  }

  if (streaming) hideStreamingTail(lines, dropIndexes);

  const kept = dropIndexes.size
    ? lines.filter((_, index) => !dropIndexes.has(index))
    : lines;
  // Collapse runs of blank lines (those left behind where a Sources block was
  // removed, too) and trim the ends. Always, so the body of a reply does not
  // change shape at the moment its Sources block first gets removed.
  const body = kept
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/^(?:[ \t]*\n)+/, "")
    .replace(/\s+$/, "");

  return { body, sources };
}

/**
 * Drop the lines at the end of a reply still being written that may yet turn
 * into a Sources block: a heading, compact line or bullet marker in progress,
 * and a numbered marker right after a Sources heading or a removed line.
 * @param lines - The reply's lines.
 * @param dropIndexes - The indexes already removed; the tail is added to it.
 */
function hideStreamingTail(lines: string[], dropIndexes: Set<number>): void {
  // Each hidden line exposes the one before it, which may be a heading that
  // only the hidden line made a block of, so look again until the last
  // visible line is settled.
  for (;;) {
    let last = lines.length - 1;
    while (last >= 0 && (dropIndexes.has(last) || lines[last].trim() === "")) last -= 1;
    if (last < 0) return;

    const line = lines[last];
    if (SOURCES_TAIL_RE.test(line)) {
      dropIndexes.add(last);
      continue;
    }
    if (!PARTIAL_NUMBER_RE.test(line)) return;
    let previous = last - 1;
    while (previous >= 0 && lines[previous].trim() === "") previous -= 1;
    if (previous < 0) return;
    if (!dropIndexes.has(previous) && !SOURCES_HEADING_RE.test(lines[previous])) return;
    for (let index = previous; index <= last; index += 1) dropIndexes.add(index);
  }
}
